/// "Final Approach Course" wedge — the chevron-shaped marker that
/// extends from the threshold outward along the localizer of an active
/// runway. Visual cue for the controller to align an aircraft on final.
///
/// Direct port of FlatoutATC/Support/GameScene.swift:280-492
/// (`addFinalApproachCourseArrow`). iOS uses scene-pixel constants at
/// 100 px/NM (arrowWidth: 40, arrowTipLength: 30, lineLength: 10 NM);
/// we express them in NM and scale to our world canvas via `pxPerNM`.
///
/// "Active" is decided by the level's `fixedWindDirection`: for each
/// runway, the end whose heading is closer to the wind (and not a
/// crosswind ≥ 90°) gets the marker. Ports the runway-selection logic
/// without porting the rest of the active-runway machinery (live wind
/// + carrier rotation come in a later phase).

import { Container, Graphics } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { Airport, LevelData, Runway } from '../types/LevelData'
import { radarTheme } from './theme'

const APPROACH_LENGTH_NM = 10
const ARROW_HALF_WIDTH_NM = 0.2 // iOS arrowWidth (40 px) / 2 / 100 px/NM
const ARROW_TIP_LENGTH_NM = 0.3 // iOS arrowTipLength (30 px) / 100 px/NM

export class ApproachCourseLayer extends Container {
  private level: LevelData
  private projector: CoordinateProjector
  private pxPerNM: number

  constructor(
    level: LevelData,
    projector: CoordinateProjector,
    pxPerNM: number,
    windDeg: number,
  ) {
    super()
    this.level = level
    this.projector = projector
    this.pxPerNM = pxPerNM
    this.rebuild(windDeg)
  }

  /// Re-evaluate active runway ends for a new wind direction and
  /// redraw. Called by the scene when the live wind sim crosses
  /// the runway-re-eval threshold. Cheap — typically a few
  /// triangles per active runway end.
  setWindDirection(windDeg: number): void {
    this.removeChildren().forEach((c) => c.destroy({ children: true }))
    this.rebuild(windDeg)
  }

  private rebuild(windDeg: number): void {
    for (const active of activeRunwayEnds(this.level, windDeg)) {
      this.addChild(buildApproach(active, this.projector, this.pxPerNM))
    }
  }
}

interface ActiveEnd {
  airport: Airport
  runway: Runway
  headingDeg: number
}

/// Mirrors iOS GameViewModel.swift:217-247 — for each runway, every
/// end whose published heading is within 90° of the wind is active.
/// That always yields at least one end per runway (the two ends are
/// 180° apart so one is always within 90° of any wind), and yields
/// both ends in a perpendicular crosswind.
function activeRunwayEnds(level: LevelData, windDeg: number): ActiveEnd[] {
  const out: ActiveEnd[] = []
  for (const airport of level.airports) {
    // Moving airports (carriers) render their approach course inside
    // CarrierLayer so it tracks the deck's motion.
    if (airport.motion) continue
    for (const runway of airport.runways) {
      for (const h of [runway.heading1, runway.heading2]) {
        const delta = Math.abs((((windDeg - h + 540) % 360) - 180))
        if (delta <= 90) {
          out.push({ airport, runway, headingDeg: h })
        }
      }
    }
  }
  return out
}

function buildApproach(
  active: ActiveEnd,
  projector: CoordinateProjector,
  pxPerNM: number,
): Container {
  const { runway, headingDeg } = active
  const node = new Container()

  const thresholdGeo =
    headingDeg === runway.heading1 ? runway.startCoordinate : runway.endCoordinate
  const otherEndGeo =
    headingDeg === runway.heading1 ? runway.endCoordinate : runway.startCoordinate
  const threshold = projector.project(thresholdGeo, true)
  const otherEnd = projector.project(otherEndGeo, true)

  // Unit vector along the approach (away from the runway's far end,
  // through the threshold, continuing into the approach corridor).
  const dx = threshold.x - otherEnd.x
  const dy = threshold.y - otherEnd.y
  const runwayLen = Math.hypot(dx, dy)
  if (runwayLen < 1) return node
  const ux = dx / runwayLen
  const uy = dy / runwayLen
  const px = -uy
  const py = ux // perpendicular (right of travel)

  const lineLength = APPROACH_LENGTH_NM * pxPerNM
  const start = threshold
  const end = { x: threshold.x + ux * lineLength, y: threshold.y + uy * lineLength }

  const totalLength = lineLength
  const maxHalfWidth = ARROW_HALF_WIDTH_NM * pxPerNM
  const arrowTipLength = ARROW_TIP_LENGTH_NM * pxPerNM
  const shoulderDist = Math.min(arrowTipLength, totalLength * 0.35)
  const headLength = Math.min(arrowTipLength, totalLength * 0.18)
  const headBaseHalfWidth = maxHalfWidth * 0.7
  const headBaseDist = Math.max(totalLength - headLength, shoulderDist + 2)
  const endHalfWidth = headBaseHalfWidth * 0.85
  const notchDepth = Math.min(arrowTipLength * 0.55, headLength * 0.75)

  const headBase = {
    x: start.x + ux * headBaseDist,
    y: start.y + uy * headBaseDist,
  }
  const leftHeadBase = {
    x: headBase.x - px * headBaseHalfWidth,
    y: headBase.y - py * headBaseHalfWidth,
  }
  const rightHeadBase = {
    x: headBase.x + px * headBaseHalfWidth,
    y: headBase.y + py * headBaseHalfWidth,
  }
  const leftEnd = { x: end.x - px * endHalfWidth, y: end.y - py * endHalfWidth }
  const rightEnd = { x: end.x + px * endHalfWidth, y: end.y + py * endHalfWidth }
  const notchPoint = { x: end.x - ux * notchDepth, y: end.y - uy * notchDepth }

  // Wedge outline: tip at threshold, V-notch cut back at far end.
  const wedge = new Graphics()
  wedge.moveTo(start.x, start.y)
  wedge.lineTo(leftHeadBase.x, leftHeadBase.y)
  wedge.lineTo(leftEnd.x, leftEnd.y)
  wedge.lineTo(notchPoint.x, notchPoint.y)
  wedge.lineTo(rightEnd.x, rightEnd.y)
  wedge.lineTo(rightHeadBase.x, rightHeadBase.y)
  wedge.closePath()
  wedge.stroke({ width: 1.5, color: radarTheme.runway, alpha: 0.35 })
  node.addChild(wedge)

  // Centerline: thin solid line from threshold to the V-notch tip.
  const center = new Graphics()
  center.moveTo(start.x, start.y)
  center.lineTo(notchPoint.x, notchPoint.y)
  center.stroke({ width: 1, color: radarTheme.runway, alpha: 0.35 })
  node.addChild(center)

  return node
}
